import { Box, TextField } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import { useMutation, useNotify } from "ra-core";
import React from "react";
import { Booking } from "../../types";
import InvoiceNumberInput from "./InvoiceNumberInput";
import InvoiceTagRadioGroup from "./InvoiceTagRadioGroup";
import { InvoiceTagValue } from "./util";

interface Props {
  data: Booking;
}

const formatSGD = (amount: number) =>
  new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency: "SGD",
  }).format(amount);

const SendInvoiceDialog: React.FC<Props> = (props) => {
  const { data } = props;
  const [open, setOpen] = React.useState(false);
  const notify = useNotify();
  const [invoiceDatas, setInvoiceDatas] = React.useState<
    { id: number; invoice_number: string; total_amount: number }[]
  >(data.booking_invoices || []); // Inisialisasi dengan data awal
  const [invoiceNumber, setInvoiceNumber] = React.useState<string>("");
  const [pendingStatus, setPendingStatus] = React.useState<InvoiceTagValue>("invoiced");

  const handleInvoiceChange = (
    id: number,
    field: "invoice_number",
    value: string
  ) => {
    const updatedInvoiceDatas = invoiceDatas.map((invoiceData) =>
      invoiceData.id === id ? { ...invoiceData, [field]: value } : invoiceData
    );
    setInvoiceDatas(updatedInvoiceDatas);
  };

  const [handleAccept] = useMutation(
    {
      type: "update",
      resource: "bookings",
      payload: {
        id: data.id ?? "",
        data: {
          action: "send_invoice",
          invoices: invoiceDatas.map((td) => ({
            id: td.id,
            invoice_number: td.invoice_number,
          })),
          invoice_number: invoiceNumber,
          invoice_tag: pendingStatus,
        },
      },
    },
    {
      onSuccess: () => {
        notify(`Invoice has been submitted successfully.`, "info");
      },
      onFailure: (error) => notify(`Error: ${error.message}`, "warning"),
    }
  );

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Button
        color="primary"
        onClick={handleClickOpen}
        size={"small"}
        variant="contained"
      >
        Submit Invoice
      </Button>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Enter the Invoice Number
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Please enter the invoice number that is created for this Booking.
          </DialogContentText>

          <InvoiceTagRadioGroup value={pendingStatus} onChange={setPendingStatus} />

          {invoiceDatas.length === 0 && (
            <InvoiceNumberInput
              value={invoiceNumber}
              onChange={setInvoiceNumber}
            />
          )}

          <Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {invoiceDatas.map((invoice, index) => (
              <Box
                key={invoice.id}
                style={{ display: "flex", alignItems: "center" }} 
              >
                {/* Total Amount */}
                <TextField
                  label={`Total Amount ${index + 1}`}
                  value={formatSGD(invoice.total_amount)}
                  variant="filled"
                  disabled
                  fullWidth
                  InputProps={{
                    style: { 
                      color: "green", 
                      fontWeight: "bold",
                      borderTopRightRadius: 0, 
                      borderBottomRightRadius: 0,
                      borderRight: "none"
                    },
                  }}
                />

                {/* Invoice Number */}
                <TextField
                  label={`Invoice Number ${index + 1}`}
                  value={invoice.invoice_number || ""}
                  variant="filled"
                  onChange={(event) =>
                    handleInvoiceChange(
                      invoice.id,
                      "invoice_number",
                      event.target.value
                    )
                  }
                  fullWidth
                  InputProps={{
                    style: { 
                      borderTopLeftRadius: 0, 
                      borderBottomLeftRadius: 0 
                    },
                  }}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAccept} color="primary">
            Submit Invoice
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SendInvoiceDialog;
